import {
  Checkbox,
  FormControl,
  FormHelperText,
  InputLabel,
  ListSubheader,
  MenuItem,
  Select,
} from "@mui/material";
import { Controller, useFormContext } from "react-hook-form";

interface SelectOption {
  value: string;
  label: string;
}

export default function ControlledSelect({
  name,
  label,
  options,
  multiple,
  required,
  showOptional,
  groupBy,
}: {
  name: string;
  label: string;
  options: SelectOption[];
  multiple?: boolean;
  required?: boolean;
  showOptional?: boolean;
  groupBy?: (option: SelectOption) => string;
}) {
  const { control } = useFormContext();
  const groupedOptions = groupBy
    ? options.reduce(
        (groups, option) => {
          const group = groupBy(option);
          if (!groups[group]) {
            groups[group] = [];
          }
          groups[group].push(option);
          return groups;
        },
        {} as Record<string, SelectOption[]>,
      )
    : {
        "": options,
      };
  return (
    <Controller
      name={name}
      control={control}
      render={({ field, fieldState }) => (
        <FormControl error={!!fieldState.error} fullWidth>
          <InputLabel id={`${name}-label`} required={required}>
            {label}
          </InputLabel>
          <Select
            {...field}
            labelId={`${name}-label`}
            label={label}
            multiple={multiple}
            renderValue={
              multiple
                ? (selected: string[]) =>
                    selected
                      ?.map((s) => options.find((o) => o.value === s)?.label)
                      .join(", ")
                : undefined
            }
            required={required}
            MenuProps={{
              style: {
                maxHeight: "calc(100vh - 100px)",
              },
            }}
            sx={{
              ".MuiInputBase-input": {
                whiteSpace: "normal !important",
              },
            }}
          >
            {Object.entries(groupedOptions).flatMap(([group, groupOptions]) => [
              group ? <ListSubheader key={group}>{group}</ListSubheader> : null,
              ...groupOptions.map((option) => (
                <MenuItem key={option.value} value={option.value}>
                  {multiple && (
                    <Checkbox
                      checked={field.value?.includes(option.value) || false}
                    />
                  )}
                  {option.label}
                </MenuItem>
              )),
            ])}
          </Select>
          <FormHelperText>
            {fieldState.error
              ? fieldState.error.message
              : showOptional && !field.value
                ? "Optional"
                : undefined}
          </FormHelperText>
        </FormControl>
      )}
    />
  );
}
